import { useMemo } from 'react';
import { usePeriodById } from '../services/period.service';
import { rollUpExtraction, rollUpTransformation } from '../utils/stageStats';
import { useGetWorkflowById } from '../services/workflow.services';
import { useGetJurisdictions } from '../services/jurisdiction.service';
import { useUserGroupsCurrentUserAdminState } from '../services/user.service';
import {
  useGetDetailedExtractionStatsByPeriodId,
  useGetDetailedTransformationStatsByPeriodId
} from '../services/funds.service';
import {
  getStatus,
  getPlaceholder,
  getStatusForPFTrans,
  getPlaceholderForPFTrans,
  getStatusForPFFinal,
  getPlaceholderForPFFinal,
  getStatusForCustomReportType,
  getPlaceholderForCustomReportType,
  getStatusForConversionReportType,
  getPlaceholderForConversionReportType,
  statusKeys
} from '../pages/Project/Quarters/data';
import { REPORT_TYPES, REPORT_NAMINGS } from '../consts/reportTypes';
import {
  useCriticalIssueCount,
  useExtractionRecordCount,
  useMappedPairCount,
  usePeriodFileCount,
  useTransformationCount
} from './useMappedPairCount';
import { FEATURE_FLAG_NAMES, useFeatureFlag } from './useFeatureFlag';
import {
  extractionNotStarted,
  hasExtractionStarted,
  mappingProgressSubLabelForPeriod,
  reportStatus
} from '../utils/periodProgress';
import { firstUnmetGate, stageGateFor } from '../utils/stageGating';
import { designPath } from '../utils/design';
import { checkToRedirect, reportStartsOnDashboard } from '../utils/checkToRedirect';

export type WorkflowStageStatus = 'done' | 'current' | 'upcoming';
export type WorkflowStageTone = 'success' | 'info' | 'warning' | 'error' | 'neutral';

// Declarative "shortcut" action attached to a stage — e.g. copy the
// previous period's jurisdictions in one click. The stepper (or its host)
// owns the mutation itself; the hook only declares what shortcuts each
// stage supports, not how to run them.
export type WorkflowStageActionKind = 'copy-jurisdictions' | 'automate-transformation';

export interface WorkflowStageAction {
  kind: WorkflowStageActionKind;
  label: string;
}

export interface WorkflowStage {
  key: string;
  label: string;
  path: string;
  status: WorkflowStageStatus;
  tone?: WorkflowStageTone;
  subLabel?: string;
  action?: WorkflowStageAction;
  /** When true, the StepItem renders a spinner next to the sub-label. */
  isLoading?: boolean;
  /** 0–1 while the stage is mid-flight and counts what it is doing; absent where it does not. */
  progress?: number;
  locked?: boolean;
  lockReason?: string;
  lockStageKey?: string;
}

export const WORKFLOW_STAGE_KEYS = {
  // `DASHBOARD` is a virtual leading stage that only appears for automation /
  // from-template periods. It links back to the project's Quarters page so
  // users can leave a period without hunting for a back button.
  DASHBOARD: 'dashboard',
  AIFS: 'aifs',
  JURISDICTIONS: 'jurisdictions',
  SOURCE_FILES: 'source_files',
  // Optional file/sheet → category mapping step, gated by the `v2_ui`
  // company flag. Excluded for Conversion report type. When omitted, the
  // mapping happens inline on the Extraction page (legacy behavior).
  SETUP: 'setup',
  EXTRACTION: 'extraction',
  TRANSFORMATION: 'transformation',
  LOOKUP_TABLES: 'lookup_tables',
  OUTPUT_DATA: 'output_data',
  REPORT: 'report',
  APPROVAL: 'approval',
  SUBMISSION: 'submission'
} as const;

export type WorkflowStageKey = (typeof WORKFLOW_STAGE_KEYS)[keyof typeof WORKFLOW_STAGE_KEYS];

export interface UseWorkflowStagesArgs {
  workflowId?: string;
  periodId?: string;
}

// Stage ordering. Conditional inclusions handled inside `useWorkflowStages`:
//   - `DASHBOARD` only renders for automation / from-template periods.
//   - `LOOKUP_TABLES` only renders when the `lookup_table` company flag is on.
//   - `SETUP` only renders when the `v2_ui` flag is on AND the report type
//     is not Conversion (Conversion's hard-coded order intentionally omits
//     it).
const STAGE_ORDER: WorkflowStageKey[] = [
  WORKFLOW_STAGE_KEYS.DASHBOARD,
  WORKFLOW_STAGE_KEYS.AIFS,
  WORKFLOW_STAGE_KEYS.JURISDICTIONS,
  WORKFLOW_STAGE_KEYS.SOURCE_FILES,
  WORKFLOW_STAGE_KEYS.SETUP,
  WORKFLOW_STAGE_KEYS.EXTRACTION,
  WORKFLOW_STAGE_KEYS.LOOKUP_TABLES,
  WORKFLOW_STAGE_KEYS.TRANSFORMATION,
  WORKFLOW_STAGE_KEYS.OUTPUT_DATA,
  WORKFLOW_STAGE_KEYS.REPORT,
  WORKFLOW_STAGE_KEYS.APPROVAL,
  WORKFLOW_STAGE_KEYS.SUBMISSION
];

// Pluralize a noun by appending "s" unless it already looks plural
// (e.g. "Fund Info" stays "Fund Info").
const pluralize = (noun: string): string => (/info|s$/i.test(noun.trim()) ? noun : `${noun}s`);

const counted = (count: number, singular: string): string =>
  `${count} ${count === 1 ? singular : pluralize(singular)}`;

/**
 * Stage labels are mostly fixed but AIFs / Jurisdictions vary by report type.
 *  - Annex IV / Conversion: "AIFs", "Jurisdictions"
 *  - Form PF: "Funds", "Fund Info"
 *  - Custom: "Assets", "Jurisdictions"
 */
export const buildStageLabels = (reportType?: string): Record<WorkflowStageKey, string> => {
  const fundSingular = (REPORT_NAMINGS?.FUND?.[reportType ?? ''] as string) ?? 'AIF';
  const jurisSingular =
    (REPORT_NAMINGS?.JURISDICTION?.[reportType ?? ''] as string) ?? 'Jurisdiction';
  return {
    [WORKFLOW_STAGE_KEYS.DASHBOARD]: 'Dashboard',
    [WORKFLOW_STAGE_KEYS.AIFS]: pluralize(fundSingular),
    [WORKFLOW_STAGE_KEYS.JURISDICTIONS]: pluralize(jurisSingular),
    [WORKFLOW_STAGE_KEYS.SOURCE_FILES]: 'Source Files',
    [WORKFLOW_STAGE_KEYS.SETUP]: 'Mapping',
    [WORKFLOW_STAGE_KEYS.EXTRACTION]: 'Extraction',
    [WORKFLOW_STAGE_KEYS.TRANSFORMATION]: 'Transformation',
    [WORKFLOW_STAGE_KEYS.LOOKUP_TABLES]: 'Lookup',
    [WORKFLOW_STAGE_KEYS.OUTPUT_DATA]: 'Output Data',
    [WORKFLOW_STAGE_KEYS.REPORT]: 'Report',
    [WORKFLOW_STAGE_KEYS.APPROVAL]: 'Approval',
    [WORKFLOW_STAGE_KEYS.SUBMISSION]: 'Submission'
  };
};

// Maps Quarters/data.jsx statusKeys to our stepper stages. Note: data.jsx's
// statusKeys.REPORT means "generate output data / report" — that work happens
// on the OutputTables page, so it maps to OUTPUT_DATA. The dedicated REPORT
// stage (viewing the actual report) has no separate status key — once the
// workflow advances past data.jsx's REPORT to APPROVAL, our REPORT stage
// auto-becomes "done" via the linear progression model.
const STATUS_TO_STAGE: Record<string, WorkflowStageKey> = {
  [statusKeys.AIF]: WORKFLOW_STAGE_KEYS.AIFS,
  [statusKeys.JURISDICTION]: WORKFLOW_STAGE_KEYS.JURISDICTIONS,
  [statusKeys.FILES]: WORKFLOW_STAGE_KEYS.SOURCE_FILES,
  [statusKeys.EXTRACTION]: WORKFLOW_STAGE_KEYS.EXTRACTION,
  [statusKeys.EXTRACTION_IN_PROGRESS]: WORKFLOW_STAGE_KEYS.EXTRACTION,
  [statusKeys.TRANSFORMATION]: WORKFLOW_STAGE_KEYS.TRANSFORMATION,
  [statusKeys.TRANSFORMATION_IN_PROGRESS]: WORKFLOW_STAGE_KEYS.TRANSFORMATION,
  [statusKeys.REPORT]: WORKFLOW_STAGE_KEYS.OUTPUT_DATA,
  [statusKeys.APPROVAL]: WORKFLOW_STAGE_KEYS.APPROVAL,
  [statusKeys.SUBMISSION]: WORKFLOW_STAGE_KEYS.SUBMISSION
};

/**
 * Compute the route for the Report stage. Mirrors `onRedirectToReportPage` in
 * src/pages/OutputTables/index.jsx:
 *   - Form PF        → /report/pf/us/:workflow/:period
 *   - Annex IV / etc → /report/{fca|esma}/{state}/:workflow/:period, picking
 *                       FCA when the first jurisdiction is UK, ESMA otherwise.
 *
 * Returns '#' when jurisdictions haven't loaded yet (link disabled).
 */
const buildReportPath = (
  workflowId: string,
  periodId: string,
  project: any,
  jurisdictions: any[] | undefined
): string => {
  if (project?.report_type === REPORT_TYPES?.FORM_PF) {
    return `/report/pf/us/${workflowId}/${periodId}`;
  }
  const first = jurisdictions?.[0];
  const state = (first?.reporting_member_state as string | undefined) ?? '';
  if (!state) return '#';
  const reportKind = state === 'UK' ? 'fca' : 'esma';
  return `/report/${reportKind}/${state.toLowerCase()}/${workflowId}/${periodId}`;
};

const buildPath = (
  key: WorkflowStageKey,
  workflowId?: string,
  periodId?: string,
  period?: any,
  project?: any,
  jurisdictions?: any[]
): string => {
  if (!workflowId || !periodId) return '#';
  // Automation / from-template periods route their Source-Files + AIFs entry
  // through the dedicated upload-files page; scratch periods use the Funds
  // page. `template_type === 'sratch'` is the scratch flow (note: backend
  // typo for "scratch" — preserved here to match the API contract).
  const isAutomation = period?.template_type && period.template_type !== 'sratch';
  /* One address per stage: each of them holds both designs and decides for itself which to draw,
     so the stepper has nothing to choose between. The report is the exception — see below. */
  switch (key) {
    case WORKFLOW_STAGE_KEYS.DASHBOARD:
      // Leading "Dashboard" stage for automation periods — back link to the
      // project's Quarters page. Carries the grouped-view query string when
      // applicable so the user lands on the same list they came from.
      return `/project/quaters/${workflowId}${project?.is_workflow_group ? '?type=grouped' : ''}`;
    case WORKFLOW_STAGE_KEYS.AIFS:
      // Both flows now use the dedicated add-aifs page for managing funds.
      // Previously scratch landed on the Funds page; AddAIFs branches on
      // `template_type === 'sratch'` internally to swap data source and hide
      // the "Previous Period" column.
      return `/project/quaters/${workflowId}/${periodId}/add-aifs`;
    case WORKFLOW_STAGE_KEYS.SOURCE_FILES:
      // Both flows use the dedicated upload-files page. UploadFiles branches
      // on `period?.previous_period_id` (`isInitialSetup`) to swap data
      // source, hide drag/expand affordances, hide the Unmap popover item,
      // etc. — see UploadFiles/index.jsx.
      return `/project/quaters/${workflowId}/${periodId}/upload-files`;
    case WORKFLOW_STAGE_KEYS.SETUP:
      return `/setup/${workflowId}/${periodId}`;
    case WORKFLOW_STAGE_KEYS.JURISDICTIONS:
      // Jurisdictions lives on a dedicated page. Automation routes nest under
      // /quaters/:id/:periodId; scratch uses the flat /:projectId namespace
      // with the periodId in the query string (matches /funds).
      return isAutomation
        ? `/project/quaters/${workflowId}/${periodId}/jurisdictions`
        : `/project/${workflowId}/jurisdictions?periodId=${periodId}`;
    case WORKFLOW_STAGE_KEYS.EXTRACTION:
      return `/data-extraction/${workflowId}/${periodId}?tab=extraction`;
    case WORKFLOW_STAGE_KEYS.TRANSFORMATION:
      return `/data-extraction/${workflowId}/${periodId}?tab=transformation`;
    case WORKFLOW_STAGE_KEYS.LOOKUP_TABLES:
      return `/data-extraction/${workflowId}/${periodId}?tab=lookup_tables`;
    case WORKFLOW_STAGE_KEYS.OUTPUT_DATA:
      return `/output-tables/${workflowId}/${periodId}`;
    case WORKFLOW_STAGE_KEYS.REPORT:
      // Plain report-viewing page. The redesigned one needs no member state in its path — it picks
      // its own jurisdiction — so there is nothing to look up before pointing at it.
      /* The one stage still at two addresses: the classic report wants a regulator and a member
         state in its path, and this has neither to give. The redesigned page finds its own. */
      // Same rule as Output's Next: an unstarted automation report starts on the dashboard.
      if (reportStartsOnDashboard(period)) return checkToRedirect(project);
      return designPath(
        `/report-v2/${workflowId}/${periodId}`,
        buildReportPath(workflowId, periodId, project, jurisdictions)
      );
    case WORKFLOW_STAGE_KEYS.APPROVAL: {
      // Approval lives on the report page as a drawer triggered by the
      // `openApprovalDrawer=true` sentinel. Clicking the Approval step
      // navigates to the same pathname with the param set; the report
      // header reads it on mount, opens the drawer, and strips the param.
      const base = designPath(
        `/report-v2/${workflowId}/${periodId}`,
        buildReportPath(workflowId, periodId, project, jurisdictions)
      );
      if (base === '#') return base;
      return base.includes('?')
        ? `${base}&openApprovalDrawer=true`
        : `${base}?openApprovalDrawer=true`;
    }
    // Submission stays on the classic report page in both designs: the sentinel below opens a modal
    // the redesigned page does not read, and a step that navigates and then does nothing is worse
    // than one that takes you somewhere that works.
    case WORKFLOW_STAGE_KEYS.SUBMISSION: {
      // Submission lives on the report page as a modal triggered by the
      // `openSubmitDrawer=true` sentinel. Each report container (ESMA / FCA
      // / FormPF) reads the sentinel and routes into its own submit flow
      // (FCA credentials → SubmitReportModal for ESMA/FCA; direct modal for
      // FormPF). The param is stripped on mount so a refresh doesn't
      // reopen the modal.
      const base = buildReportPath(workflowId, periodId, project, jurisdictions);
      if (base === '#') return base;
      return base.includes('?') ? `${base}&openSubmitDrawer=true` : `${base}?openSubmitDrawer=true`;
    }
    default:
      return '#';
  }
};

/**
 * Pick the right (status, placeholder) deriver pair from Quarters/data.jsx
 * based on the workflow's report type and the period's filing type.
 */
const pickStatusFns = (
  project: any,
  period: any
): {
  getStatusFn: (p: any) => string;
  getPlaceholderFn: (p: any) => string | null | undefined;
} => {
  if (period?.filing_type === 'PF_TRANS') {
    return { getStatusFn: getStatusForPFTrans, getPlaceholderFn: getPlaceholderForPFTrans };
  }
  if (period?.filing_type === 'PF_FINAL') {
    return { getStatusFn: getStatusForPFFinal, getPlaceholderFn: getPlaceholderForPFFinal };
  }
  if (project?.report_type === REPORT_TYPES?.CONVERSION) {
    return {
      getStatusFn: getStatusForConversionReportType,
      getPlaceholderFn: getPlaceholderForConversionReportType
    };
  }
  if (project?.report_type === REPORT_TYPES?.CUSTOM) {
    return {
      getStatusFn: getStatusForCustomReportType,
      getPlaceholderFn: getPlaceholderForCustomReportType
    };
  }
  return {
    getStatusFn: p => getStatus(p, project?.report_type),
    getPlaceholderFn: p => getPlaceholder(p, project?.report_type)
  };
};

const buildCountSubLabel = (
  key: WorkflowStageKey,
  period: any,
  labels: Record<WorkflowStageKey, string>,
  reportType?: string
): string | undefined => {
  const fundSingular = (REPORT_NAMINGS?.FUND?.[reportType ?? ''] as string) ?? 'AIF';
  const jurisSingular =
    (REPORT_NAMINGS?.JURISDICTION?.[reportType ?? ''] as string) ?? 'Jurisdiction';

  switch (key) {
    case WORKFLOW_STAGE_KEYS.AIFS:
      return period?.fund_count != null ? counted(period.fund_count, fundSingular) : undefined;
    case WORKFLOW_STAGE_KEYS.JURISDICTIONS:
      return period?.jurisdiction_count != null
        ? counted(period.jurisdiction_count, jurisSingular.toLowerCase())
        : undefined;
    case WORKFLOW_STAGE_KEYS.SOURCE_FILES:
      return period?.file_count > 0 ? counted(period.file_count, 'file') : undefined;
    case WORKFLOW_STAGE_KEYS.EXTRACTION: {
      // Four visible states:
      //   • not-started → undefined here; caller replaces the placeholder
      //     with "Start Extraction". A pool of not_completed sheets on its
      //     own does NOT count as started — only concrete activity
      //     (successful / failed / warned / in_progress) does.
      //   • in-flight  → "Extracting X/Y" with a spinner (isLoading flag
      //     is set in the map iteration below).
      //   • idle-partial → "Extracted X/Y" (no spinner). Some items finished,
      //     nothing is currently running, but the pool isn't fully drained
      //     (e.g. sheets left in `not_completed` after a partial run).
      //     Previously we always painted "Extracting X/Y" here — the stepper
      //     then spun forever on a queue that wasn't moving.
      //   • finished   → "Finished".
      const stats = period?.extraction_stats;
      if (!stats) return undefined;
      const successful = stats.successful_extractions_number ?? 0;
      const failed = stats.failed_extractions_number ?? 0;
      const warned = stats.warned_extractions_number ?? 0;
      const inProgress = stats.in_progress_extractions_number ?? 0;
      const notCompleted = stats.not_completed_extractions_number ?? 0;
      const hasActivity = successful + failed + warned + inProgress > 0;
      if (!hasActivity) return undefined;
      // "Processed" = anything the pipeline is done with, success OR failure.
      // Failed items count toward the numerator so the fraction reflects
      // pipeline progress ("we're 3/5 of the way through this batch"), not
      // just the healthy-path count. The failure surfacing below still
      // appends "· N failed" and flips the tone to red, so the user sees
      // both the total progress and the failure count at a glance.
      const done = successful + warned + failed;
      const total = done + inProgress + notCompleted;
      if (total === 0) return undefined;
      if (done === total) return 'Finished';
      // Only claim "Extracting" (spinner-on) when something is actually
      // running. `startsWith('Extracting')` in the isLoading gate below
      // then correctly excludes the idle "Extracted" label.
      if (inProgress > 0) return `Extracting ${done}/${total}`;
      return `Extracted ${done}/${total}`;
    }
    case WORKFLOW_STAGE_KEYS.TRANSFORMATION: {
      const stats = period?.transformation_stats;
      if (!stats) return undefined;
      const successful = stats.successful_transformations_number ?? 0;
      const failed = stats.failed_transformations_number ?? 0;
      const inProgress = stats.in_progress_transformation_number ?? 0;
      const notCompleted = stats.not_completed_transformations_number ?? 0;
      const hasActivity = successful + failed + inProgress > 0;
      if (!hasActivity) return undefined;
      // See EXTRACTION above — failed transformations count as processed,
      // and "Transforming" is reserved for the in-flight case only.
      const done = successful + failed;
      const total = done + inProgress + notCompleted;
      if (total === 0) return undefined;
      if (done === total) return 'Finished';
      if (inProgress > 0) return `Transforming ${done}/${total}`;
      return `Transformed ${done}/${total}`;
    }
    default:
      return undefined;
  }
};

/**
 * Returns 8 workflow stages with statuses derived from Quarters/data.jsx.
 *
 * Model: at any point one stage is `current` (the next thing to do); earlier
 * stages are `done`, later stages are `upcoming`. For PF_TRANS / PF_FINAL
 * filings the leading stages (AIFs/Jurisdictions/Source Files/Extraction/
 * Transformation) auto-collapse to `done` because those status fns skip them.
 */
export const useWorkflowStages = (
  args?: UseWorkflowStagesArgs
): { stages: WorkflowStage[]; currentStageIndex: number; isLoading: boolean } => {
  const workflowId = args?.workflowId;
  const periodId = args?.periodId;
  const isLookupEnabled = useFeatureFlag(FEATURE_FLAG_NAMES.LOOKUP_TABLE);
  const isSetupEnabled = useFeatureFlag(FEATURE_FLAG_NAMES.V2_UI);

  // Refreshed by `useWorkflowChromeStatusSync`: on each live event, and on its missed-event beat.
  const { data: period, isLoading: isLoadingPeriod } = usePeriodById({
    periodId,
    queryParams: { enabled: !!periodId }
  }) as { data?: any; isLoading: boolean };

  const { data: project, isLoading: isLoadingProject } = useGetWorkflowById({
    id: workflowId,
    queryParams: { enabled: !!workflowId }
  }) as { data?: any; isLoading: boolean };

  const { data: currentUser, isLoading: isLoadingCurrentUser } =
    useUserGroupsCurrentUserAdminState() as { data?: { role?: string }; isLoading: boolean };
  const roleAllowsCounts = !isLoadingCurrentUser && currentUser?.role !== 'Approver';

  /* What this period could extract. Shared with the dashboard, which shows the same statuses one
     click away and disagreed with this stepper for as long as it lacked the count. */
  const mappedPairCount = useMappedPairCount(periodId);
  /* And how many it actually holds. `extraction_stats` is absent on periods plainly holding
     extractions, so counting the saved records is the only way to tell an unextracted period from
     one whose stats never arrived — the difference between "Start Extraction" and "Finished". */
  const extractionRecordCount = useExtractionRecordCount(workflowId, periodId, roleAllowsCounts);
  const periodFileCount = usePeriodFileCount(periodId, roleAllowsCounts);
  /* Automation periods keep their transformation actions somewhere else entirely — see the hook. */
  const transformationCount = useTransformationCount(
    periodId,
    !!period?.template_type && period.template_type !== 'sratch',
    roleAllowsCounts
  );
  const criticalIssueCount = useCriticalIssueCount(
    periodId,
    hasExtractionStarted({ ...period, extractionRecordCount })
  );

  // Needed to compute the Report stage URL (FCA vs ESMA depends on the first
  // jurisdiction's reporting_member_state). Form PF doesn't depend on this.
  const { data: jurisdictions, isLoading: isLoadingJurisdictions } = useGetJurisdictions({
    periodId,
    queryParams: { enabled: !!periodId }
  }) as { data?: any[]; isLoading: boolean };

  // Automation periods use the same endpoints as the ExtractionIssues /
  // TransformationIssues modals — those endpoints refresh instantly on
  // SignalR events, whereas the period record's rolled-up
  // `extraction_stats` / `transformation_stats` can lag by a few seconds
  // after backend transitions. Fetching the live records lets the stepper's
  // "X/Y extracted" sub-label match the modal's numbers exactly.
  const isAutomationPeriod = !!period?.template_type && period.template_type !== 'sratch';
  const { data: extractionRecords } = useGetDetailedExtractionStatsByPeriodId({
    periodId,
    queryParams: { enabled: !!periodId && isAutomationPeriod }
  }) as { data?: any[] };
  const { data: transformationRecords } = useGetDetailedTransformationStatsByPeriodId({
    periodId,
    queryParams: { enabled: !!periodId && isAutomationPeriod }
  }) as { data?: any[] };

  // Live updates come from `useWorkflowChromeStatusSync`, which refreshes these same queries once per event burst.

  // Aggregate the per-sheet detailed records into count buckets that
  // match the shape of `period.extraction_stats` — that way
  // `buildCountSubLabel` can consume either source without branching.
  // Handles both raw-array responses AND `{ data: [...] }` wrappers
  // just in case the interceptor doesn't unwrap.
  const liveExtractionStats = useMemo(
    () => rollUpExtraction(extractionRecords),
    [extractionRecords]
  );
  const liveTransformationStats = useMemo(
    () => rollUpTransformation(transformationRecords),
    [transformationRecords]
  );

  const stages = useMemo<WorkflowStage[]>(() => {
    if (!periodId || !period) return [];

    const stageLabels = buildStageLabels(project?.report_type);
    const { getStatusFn, getPlaceholderFn } = pickStatusFns(project, period);
    const currentStatusKey = getStatusFn(period);
    const placeholder = getPlaceholderFn(period) ?? undefined;

    /* Measured before anything is read off the record, because it can contradict all of it. A period
       whose stats are missing a field walks straight through `getStatus`'s guards — `undefined === 0`
       is false (Quarters/data.jsx:173) — and comes out the far end as END, which paints every chip
       done including an Extraction that never ran. Conversion has no Extraction stage to speak for. */
    const extractionHasNotStarted =
      project?.report_type !== REPORT_TYPES?.CONVERSION &&
      extractionNotStarted({
        extraction_stats: liveExtractionStats ?? period?.extraction_stats,
        file_sheet_stats: period?.file_sheet_stats,
        mappedPairCount,
        extractionRecordCount
      });

    // statusKeys.END means everything is done — no current stage.
    let isAllDone = currentStatusKey === statusKeys.END && !extractionHasNotStarted;
    let currentStageKey = isAllDone ? null : (STATUS_TO_STAGE[currentStatusKey] ?? null);

    // Every included fund's national code is Nil Return — there is no Output Data step for the
    // cursor to land on (OUTPUT_DATA is filtered out of `orderedKeys` below), so anywhere the
    // cursor would otherwise sit on OUTPUT_DATA it needs to be redirected to REPORT instead.
    // Nil Return is Annex IV-only; other report types keep their normal stage sequence.
    const isNilReturn =
      period?.all_national_codes_nil_return === true &&
      project?.report_type === REPORT_TYPES?.ANNEX_IV;

    const reportState = reportStatus(period);
    const isReportGenerated = reportState === 'generated';
    const isReportInProgress = reportState === 'generating';
    const isReportFailed = reportState === 'failed';

    // When the Setup step is enabled (and not Conversion), divert the cursor
    // from EXTRACTION back to SETUP whenever any source-file sheet is still
    // unmapped. Client-derived until the backend exposes a dedicated
    // `setup_progress_status` field — swap to authoritative status with no
    // UI change once it lands.
    const isConversionReport = project?.report_type === REPORT_TYPES?.CONVERSION;
    const hasUnmappedSheets = (period?.file_sheet_stats?.not_mapped_sheet_count ?? 0) > 0;
    /* Once anything has been extracted the diversion stops: the Mapping chip reads "Finished" off
       the same fact, and a step cannot be finished and still hold the cursor — that combination
       greys out Extraction as `upcoming` while it is visibly counting up. */
    const mappingSettled = hasExtractionStarted({
      extraction_stats: liveExtractionStats ?? period?.extraction_stats,
      extraction_progress_status: period?.extraction_progress_status
    });
    if (
      isSetupEnabled &&
      !isConversionReport &&
      hasUnmappedSheets &&
      !mappingSettled &&
      (currentStageKey === WORKFLOW_STAGE_KEYS.EXTRACTION ||
        currentStageKey === WORKFLOW_STAGE_KEYS.TRANSFORMATION)
    ) {
      currentStageKey = WORKFLOW_STAGE_KEYS.SETUP;
    }

    // The status fns stick at `REPORT` whenever the report file isn't
    // generated OR comments aren't fully resolved — that maps to the
    // OUTPUT_DATA stage by default. Refine the cursor placement based on
    // what's actually happening on the report side:
    //   - report file built → cursor → APPROVAL (resolve-comments lives there)
    //   - report task in flight / failed → cursor → REPORT (surface progress)
    //   - report not started yet → leave cursor on OUTPUT_DATA (Generate Report)
    if (
      currentStatusKey === statusKeys.REPORT &&
      currentStageKey === WORKFLOW_STAGE_KEYS.OUTPUT_DATA
    ) {
      if (isReportGenerated) {
        currentStageKey = WORKFLOW_STAGE_KEYS.APPROVAL;
      } else if (isReportInProgress || isReportFailed) {
        currentStageKey = WORKFLOW_STAGE_KEYS.REPORT;
      } else if (project?.report_type === REPORT_TYPES?.CONVERSION) {
        // Conversion's ordered list doesn't include OUTPUT_DATA — its
        // sequence is … Jurisdictions → Report → Approval → Submission.
        // Snap the cursor onto the visible REPORT stage; otherwise
        // `filteredCurrentKey` falls back to the first non-Dashboard
        // stage (Source Files) and the earlier chips render as current
        // even though setup is complete.
        currentStageKey = WORKFLOW_STAGE_KEYS.REPORT;
      } else if (period?.transformation_progress_status === 'Completed') {
        // Once transformation is done, the intermediate Output Data step is
        // auto-completed by the backend as the report is
        // generated. Move the cursor straight to REPORT so the user isn't
        // stuck on Output Data waiting for something they can't act on.
        currentStageKey = WORKFLOW_STAGE_KEYS.REPORT;
      } else if (isNilReturn) {
        currentStageKey = WORKFLOW_STAGE_KEYS.REPORT;
      }
    }

    // Live-stats often flip to "Finished" for a stage seconds before
    // the aggregate `*_progress_status` field on the period record
    // catches up. If we leave `currentStageKey` on that stage, the
    // linear progression turns EVERY following stage into `upcoming`
    // with no sub-label (renders as em-dash). Advance the cursor to
    // the next real stage so the follow-on chip becomes the proper
    // current step.
    const extractionCountsSayDone = liveExtractionStats
      ? (() => {
          const done =
            liveExtractionStats.successful_extractions_number +
            liveExtractionStats.warned_extractions_number;
          const total =
            done +
            liveExtractionStats.failed_extractions_number +
            liveExtractionStats.in_progress_extractions_number +
            liveExtractionStats.not_completed_extractions_number;
          return total > 0 && done === total;
        })()
      : false;
    const transformationCountsSayDone = liveTransformationStats
      ? (() => {
          const done = liveTransformationStats.successful_transformations_number;
          const total =
            done +
            liveTransformationStats.failed_transformations_number +
            liveTransformationStats.in_progress_transformation_number +
            liveTransformationStats.not_completed_transformations_number;
          return total > 0 && done === total;
        })()
      : false;
    if (currentStageKey === WORKFLOW_STAGE_KEYS.EXTRACTION && extractionCountsSayDone) {
      currentStageKey = WORKFLOW_STAGE_KEYS.TRANSFORMATION;
    }
    if (currentStageKey === WORKFLOW_STAGE_KEYS.TRANSFORMATION && transformationCountsSayDone) {
      currentStageKey = WORKFLOW_STAGE_KEYS.REPORT;
    }

    /* The counterpart of the two rules above: they move the cursor on when the counts say a stage is
       done, this holds it back when they say a stage has not begun. `getStatus` walks past Extraction
       on a period whose stats are missing a field — `undefined === 0` is false, so its
       "nothing succeeded yet" guard does not catch it (Quarters/data.jsx:173) — and every stage to
       the left of the cursor is then painted done, which is how "Finished" appeared over an
       extraction list counting 0 of 7. Conversion has no Extraction stage to hold. */
    /* `null` is the END case the veto above already rejected — there is no cursor to compare, and
       Extraction is where it belongs. Only ever pulls the cursor back: a stage before Extraction
       (Mapping with sheets still unmapped, say) is earlier work and keeps it. */
    const cursorIsPastExtraction =
      currentStageKey === null ||
      STAGE_ORDER.indexOf(currentStageKey as WorkflowStageKey) >
        STAGE_ORDER.indexOf(WORKFLOW_STAGE_KEYS.EXTRACTION);
    if (extractionHasNotStarted && cursorIsPastExtraction) {
      currentStageKey = WORKFLOW_STAGE_KEYS.EXTRACTION;
    }

    const gatePeriod = {
      ...period,
      extraction_stats: liveExtractionStats ?? period.extraction_stats,
      transformation_stats: liveTransformationStats ?? period.transformation_stats,
      mappedPairCount,
      extractionRecordCount,
      periodFileCount,
      criticalIssueCount
    };
    const gatingOptions = {
      period: gatePeriod,
      reportType: project?.report_type,
      hasMapping: isSetupEnabled,
      enabled: currentUser?.role !== 'Approver'
    };
    const blockingGate = firstUnmetGate(gatingOptions);
    const gateStageKey = blockingGate?.stageKey as WorkflowStageKey | undefined;
    const gateIsEarlier =
      !!gateStageKey &&
      (currentStageKey === null ||
        STAGE_ORDER.indexOf(gateStageKey) <
          STAGE_ORDER.indexOf(currentStageKey as WorkflowStageKey));
    if (gateIsEarlier) {
      currentStageKey = gateStageKey;
      isAllDone = false;
    }

    const isInProgressVariant =
      currentStatusKey === statusKeys.EXTRACTION_IN_PROGRESS ||
      currentStatusKey === statusKeys.TRANSFORMATION_IN_PROGRESS;
    // `DASHBOARD` renders when EITHER the current period is automation /
    // from-template OR the workflow has at least two periods. Both cases
    // benefit from a quick back link to the Quarters page — automation
    // periods because they're nested under /quaters/:id, multi-period
    // workflows because the user may want to jump between siblings.
    const isAutomation = period?.template_type && period.template_type !== 'sratch';
    const hasMultiplePeriods = (project?.periods?.length ?? 0) >= 2;
    // Also render Dashboard once the report has been generated — at that
    // point the user is in review territory and benefits from a quick way
    // back to the project's Quarters page.
    const showDashboard = isAutomation || hasMultiplePeriods || isReportGenerated;

    let orderedKeys = isLookupEnabled
      ? STAGE_ORDER
      : STAGE_ORDER.filter(k => k !== WORKFLOW_STAGE_KEYS.LOOKUP_TABLES);
    if (!isSetupEnabled) {
      orderedKeys = orderedKeys.filter(k => k !== WORKFLOW_STAGE_KEYS.SETUP);
    }
    if (!showDashboard) {
      orderedKeys = orderedKeys.filter(k => k !== WORKFLOW_STAGE_KEYS.DASHBOARD);
    }
    // When every included fund's national code in this period is Nil Return there is
    // nothing to upload/extract/transform/model — skip straight from Jurisdictions to
    // Report (mirrors the PF_TRANS/PF_FINAL shortcut above, different trigger).
    if (isNilReturn) {
      const nilReturnSkippedKeys = new Set<WorkflowStageKey>([
        WORKFLOW_STAGE_KEYS.SOURCE_FILES,
        WORKFLOW_STAGE_KEYS.SETUP,
        WORKFLOW_STAGE_KEYS.EXTRACTION,
        WORKFLOW_STAGE_KEYS.TRANSFORMATION,
        WORKFLOW_STAGE_KEYS.LOOKUP_TABLES,
        WORKFLOW_STAGE_KEYS.OUTPUT_DATA
      ]);
      orderedKeys = orderedKeys.filter(k => !nilReturnSkippedKeys.has(k));
    }
    // Conversion report type uses a distinct visual sequence:
    // Dashboard → Source Files → AIFs → Jurisdictions → Report → Approval →
    // Submission. Diverges from STAGE_ORDER (which puts AIFs / Jurisdictions
    // before Source Files); aligns with getStatusForConversionReportType in
    // Quarters/data.jsx, which already checks FILES first → AIF → JURISDICTION
    // → REPORT → APPROVAL → SUBMISSION. Hard-coded order rather than a filter
    // so the divergent sequence is explicit.
    if (project?.report_type === REPORT_TYPES?.CONVERSION) {
      // Conversion always leads with Dashboard. After a Jurisdictions save we
      // route the user back to the Quarters page, so the back-link belongs in
      // the stepper regardless of automation / multi-period / report-generated
      // gates that apply to other report types.
      orderedKeys = [
        WORKFLOW_STAGE_KEYS.DASHBOARD,
        WORKFLOW_STAGE_KEYS.SOURCE_FILES,
        WORKFLOW_STAGE_KEYS.AIFS,
        WORKFLOW_STAGE_KEYS.JURISDICTIONS,
        WORKFLOW_STAGE_KEYS.REPORT,
        WORKFLOW_STAGE_KEYS.APPROVAL,
        WORKFLOW_STAGE_KEYS.SUBMISSION
      ];
    }
    const fullOrder = orderedKeys;
    if (currentUser?.role === 'Approver') {
      const allowed = new Set<WorkflowStageKey>([
        WORKFLOW_STAGE_KEYS.OUTPUT_DATA,
        WORKFLOW_STAGE_KEYS.REPORT
      ]);
      orderedKeys = orderedKeys.filter(k => allowed.has(k));
    }

    // If the workflow's actual current stage was filtered out (e.g. an Approver
    // looking at a workflow whose cursor is on AIFs), pin the first remaining
    // stage as "current" so the stepper isn't entirely blank or stuck on a
    // hidden status.
    // A hidden stage past everything shown (an approver's Approval) makes the shown ones done, not the first one current.
    const currentBeyondShown =
      !!currentStageKey &&
      !orderedKeys.includes(currentStageKey as WorkflowStageKey) &&
      orderedKeys.length > 0 &&
      fullOrder.indexOf(currentStageKey as WorkflowStageKey) >
        fullOrder.indexOf(orderedKeys[orderedKeys.length - 1]);
    const filteredCurrentKey = currentBeyondShown
      ? null
      : currentStageKey && orderedKeys.includes(currentStageKey as WorkflowStageKey)
        ? (currentStageKey as WorkflowStageKey)
        : (orderedKeys.find(k => k !== WORKFLOW_STAGE_KEYS.DASHBOARD) ?? null);
    let passedCurrent = false;

    return orderedKeys.map(key => {
      let status: WorkflowStageStatus;
      let tone: WorkflowStageTone;

      // Dashboard is a back-link, not a real workflow stage — always render
      // it as `done`/success so it sits left-of-current without breaking the
      // linear progression model.
      if (key === WORKFLOW_STAGE_KEYS.DASHBOARD) {
        return {
          key,
          label: stageLabels[key],
          path: buildPath(key, workflowId, periodId, period, project, jurisdictions),
          status: 'done' as WorkflowStageStatus,
          tone: 'success' as WorkflowStageTone,
          subLabel: undefined
        };
      }

      if (isAllDone) {
        status = 'done';
        tone = 'success';
      } else if (key === filteredCurrentKey) {
        status = 'current';
        tone =
          currentStatusKey === statusKeys.APPROVAL
            ? 'warning'
            : isInProgressVariant
              ? 'info'
              : 'info';
        passedCurrent = true;
      } else if (!passedCurrent) {
        status = 'done';
        tone = 'success';
      } else {
        status = 'upcoming';
        tone = 'neutral';
      }

      // Prefer the live detailed-stats aggregates on automation periods —
      // the period record's rolled-up stats can lag behind SignalR events.
      const periodForCounts =
        liveExtractionStats || liveTransformationStats
          ? {
              ...period,
              extraction_stats: liveExtractionStats ?? period.extraction_stats,
              transformation_stats: liveTransformationStats ?? period.transformation_stats
            }
          : period;
      const setupAt = orderedKeys.indexOf(WORKFLOW_STAGE_KEYS.SETUP);
      // Blocked until Source Files is done, not merely reached — same as the periods list.
      // "Upload Files" still on screen there means the sheets this step would read don't exist
      // yet. `isAllDone` is checked explicitly because it sets `currentStageKey` to null, and
      // `filteredCurrentKey` then falls back to the array's first key (AIFs) as a display default —
      // read positionally, a done period would otherwise look like it never left AIFs and hide
      // Mapping's real "Finished".
      const sourceFilesAt = orderedKeys.indexOf(WORKFLOW_STAGE_KEYS.SOURCE_FILES);
      const currentAt = filteredCurrentKey ? orderedKeys.indexOf(filteredCurrentKey) : -1;
      const mappingReachable =
        isAllDone || sourceFilesAt < 0 || currentAt < 0 || currentAt > sourceFilesAt;
      // Only before mapping begins: nothing downstream can be finished yet.
      const mappingUnfinished =
        setupAt >= 0 && period?.category_mapping_progress_status === 'Not started';
      const countLabel = buildCountSubLabel(
        key,
        periodForCounts,
        stageLabels,
        project?.report_type
      );
      // The REPORT stage represents viewing the generated report — once the
      // user is on it, no actionable sub-label is needed. Skip the "Resolve
      // comments first" / "Generate Report" placeholders for this stage.
      const useStatusPlaceholder =
        key === filteredCurrentKey &&
        placeholder &&
        key !== WORKFLOW_STAGE_KEYS.REPORT &&
        // …and not an invitation that cannot be accepted. "Start Transformation" over a period
        // where no sheet is mapped offers work on data that does not exist: nothing is extracted,
        // so there is nothing to transform. The chip goes quiet with the rest of the tail.
        !(
          mappingUnfinished &&
          key !== WORKFLOW_STAGE_KEYS.SETUP &&
          orderedKeys.indexOf(key) > setupAt
        );
      // "Approved" reads more accurately than "Finished" on the Approval
      // chip once the phase is done — matches the domain language everywhere
      // else in the reviewer flow (Approved / Pending status dots, "Approve
      // AIF Report" button, etc.).
      const doneLabel = key === WORKFLOW_STAGE_KEYS.APPROVAL ? 'Approved' : 'Finished';
      // Measured, unlike every other chip's positional "done".
      const setupStatus =
        key === WORKFLOW_STAGE_KEYS.SETUP && mappingReachable
          ? mappingProgressSubLabelForPeriod({
              ...periodForCounts,
              mappedPairCount,
              extractionRecordCount
            })
          : undefined;
      // The colour follows the word, not the cursor position.
      const setupTone: WorkflowStageTone | undefined =
        setupStatus === 'Finished' ? 'success' : setupStatus === 'In progress' ? 'info' : undefined;
      // A stage after Mapping cannot be finished while Mapping is not. "Finished" on those chips is
      // inferred from the cursor sitting further right, not measured — and with sheets still
      // unmapped there is no way to know how much work is even left. A measured count still shows
      // (`Extracted 3 of 5` is a fact); only the inferred word is withheld.
      const afterUnfinishedMapping =
        mappingUnfinished &&
        key !== WORKFLOW_STAGE_KEYS.SETUP &&
        orderedKeys.indexOf(key) > setupAt;
      let subLabel: string | undefined =
        key === WORKFLOW_STAGE_KEYS.SETUP
          ? setupStatus
          : useStatusPlaceholder
            ? placeholder
            : status === 'done'
              ? (countLabel ?? (afterUnfinishedMapping ? undefined : doneLabel))
              : countLabel;
      if (setupTone && subLabel) tone = setupTone;
      if (afterUnfinishedMapping && !subLabel) tone = 'neutral';

      // When EXTRACTION or TRANSFORMATION is the current stage on an
      // automation / from-template period, surface the "X/Y extracted" /
      // "X/Y transformed" fraction instead of the generic "in progress"
      // placeholder. Scratch periods keep the original placeholder
      // because their stats are driven by user-created transformations,
      // not a bulk pipeline where a running total is meaningful.
      // Reuses `isAutomation` computed once at the top of the useMemo.
      if (
        isAutomation &&
        status === 'current' &&
        countLabel &&
        (key === WORKFLOW_STAGE_KEYS.EXTRACTION || key === WORKFLOW_STAGE_KEYS.TRANSFORMATION)
      ) {
        subLabel = countLabel;
      }

      if (status === 'current' && countLabel && key === WORKFLOW_STAGE_KEYS.SOURCE_FILES) {
        subLabel = countLabel;
      }

      // Live-stats can flip to "Finished" before the aggregate
      // `extraction_progress_status` / `transformation_progress_status`
      // field catches up on the backend — during that window the stage
      // reads as `current` (blue) while its sub-label already says
      // "Finished". Force it to done+success so the tone matches the
      // reality visible in the sub-label.
      if (
        isAutomation &&
        status === 'current' &&
        countLabel === 'Finished' &&
        (key === WORKFLOW_STAGE_KEYS.EXTRACTION || key === WORKFLOW_STAGE_KEYS.TRANSFORMATION)
      ) {
        status = 'done';
        tone = 'success';
      }

      /* The mirror of the rule above. The cursor is positional, so every chip left of it is painted
         done — but these two measure themselves, and a run that is still moving is not done however
         far right the cursor has gone. Without this the chip reads green "Extracting 0/5": finished
         and in flight at once. Left before the failure block so red still wins. */
      if (
        isAutomation &&
        (key === WORKFLOW_STAGE_KEYS.EXTRACTION || key === WORKFLOW_STAGE_KEYS.TRANSFORMATION) &&
        typeof countLabel === 'string' &&
        (countLabel.startsWith('Extracting') || countLabel.startsWith('Transforming'))
      ) {
        tone = 'info';
      }

      /* Failure surfacing — append "· N failed" and flip the tone to red, so problems are visible
         without opening the Issues drawer. Runs regardless of status so failures stay on both
         `current` and `done` chips, and regardless of period type: a running total is only
         meaningful for a bulk pipeline, but a sheet that failed is worth saying on any period.
         Absent stats read as 0, so a period that carries none renders exactly as before. */
      if (key === WORKFLOW_STAGE_KEYS.EXTRACTION) {
        const failed = periodForCounts?.extraction_stats?.failed_extractions_number ?? 0;
        if (failed > 0) {
          subLabel = subLabel ? `${subLabel} · ${failed} failed` : `${failed} failed`;
          tone = 'error';
        }
      } else if (key === WORKFLOW_STAGE_KEYS.TRANSFORMATION) {
        const failed = periodForCounts?.transformation_stats?.failed_transformations_number ?? 0;
        if (failed > 0) {
          subLabel = subLabel ? `${subLabel} · ${failed} failed` : `${failed} failed`;
          tone = 'error';
        }
      }

      // The placeholder fns in Quarters/data.jsx return hardcoded "Add Funds"
      // / "Add Jurisdictions" strings for the AIFS / JURISDICTIONS stages —
      // those don't follow the per-report-type naming (e.g. Form PF's
      // "Fund Info" label, Custom's "Assets"). Swap to `Add ${stageLabel}` so
      // the sub-label reads "Add Fund Info" for Form PF instead of
      // "Add Jurisdictions". Only fires when the current placeholder is the
      // generic noun — other placeholder strings (Upload Files, Create
      // Extraction, etc.) pass through untouched.
      if (
        useStatusPlaceholder &&
        (key === WORKFLOW_STAGE_KEYS.JURISDICTIONS || key === WORKFLOW_STAGE_KEYS.AIFS)
      ) {
        subLabel = `Add ${stageLabels[key]}`;
      }

      // Nicer copy for the EXTRACTION / TRANSFORMATION not-started states.
      // The generic placeholders read "Create Extraction" / "Create
      // Transformation"; the stepper prefers action-shaped labels so the
      // user reads it as "here's the button, click me".
      if (key === filteredCurrentKey && key === WORKFLOW_STAGE_KEYS.EXTRACTION && !countLabel) {
        subLabel = 'Start Extraction';
      } else if (
        key === filteredCurrentKey &&
        key === WORKFLOW_STAGE_KEYS.TRANSFORMATION &&
        !countLabel
      ) {
        // Automation periods hold their copied actions before any run, so a count proves nothing there.
        if (transformationCount && !isAutomation) {
          subLabel = 'Finished';
          status = 'done';
          tone = 'success';
        } else {
          subLabel = 'Start Transformation';
        }
      }

      /* The muting above runs before the two action labels are assigned, so it judged this chip on
         a sub-label it did not yet have and greyed the step the user is being asked to act on.
         Re-read once the label is final: the step the workflow is waiting on is never neutral. */
      if (status === 'current' && subLabel && tone === 'neutral') {
        tone = 'info';
      }

      // Spinner beside the sub-label whenever the pipeline is mid-flight
      // (the countLabel starts with "Extracting" / "Transforming"). Rendered
      // by StepItem — pure UI concern, doesn't affect the label text.
      let isLoading =
        (key === WORKFLOW_STAGE_KEYS.EXTRACTION || key === WORKFLOW_STAGE_KEYS.TRANSFORMATION) &&
        typeof countLabel === 'string' &&
        (countLabel.startsWith('Extracting') || countLabel.startsWith('Transforming'));

      /* How far through it is, for the ring the step draws around itself. */
      const counted = isLoading ? /(\d+)\s*\/\s*(\d+)/.exec(countLabel as string) : null;
      const progress =
        counted && Number(counted[2]) > 0 ? Number(counted[1]) / Number(counted[2]) : undefined;

      // Same order as `reportStatus`: Generating, Generated, Failed, then Generate if next.
      if (key === WORKFLOW_STAGE_KEYS.REPORT) {
        if (isReportInProgress) {
          subLabel = 'Generating report...';
          tone = 'info';
          /* A report takes minutes and counts nothing while it does. */
          isLoading = true;
        } else if (isReportGenerated) {
          subLabel = 'Generated';
          status = 'done';
          tone = 'success';
        } else if (isReportFailed) {
          subLabel = 'Failed';
          tone = 'warning';
        } else if (status === 'current') {
          subLabel = 'Generate Report';
          tone = 'info';
        }
      }

      // When the cursor lands on APPROVAL purely because the report file is
      // generated (status fn still at REPORT due to comments), the inherited
      // placeholder text is "Resolve comments first" — that's REPORT-phase
      // advice on an APPROVAL-phase stage. Replace it with the approval
      // placeholder so the sub-label fits the stage the user actually sees.
      if (
        key === WORKFLOW_STAGE_KEYS.APPROVAL &&
        key === filteredCurrentKey &&
        isReportGenerated &&
        currentStatusKey === statusKeys.REPORT
      ) {
        subLabel = 'Approval Pending';
      }

      // Report / Approval / Submission are gated on actual workflow progress
      // — mirrors the Quarters table's ReportColumn / SubmissionColumn rules.
      // When the workflow hasn't reached the corresponding phase yet, fall
      // through to path = '#' so the stepper renders the button disabled
      // instead of letting the user click and land on a non-functional page.
      let path = buildPath(key, workflowId, periodId, period, project, jurisdictions);
      if (key === WORKFLOW_STAGE_KEYS.REPORT) {
        // Viewing the generated report is unlocked once the report exists
        // (covered by `isReportGenerated`, which also handles scratch
        // periods that never get a separate report_task) OR the workflow
        // has advanced past report generation to APPROVAL / SUBMISSION / END.
        // Also unlocked once transformation is complete — Output Data
        // auto-progresses once the pipeline finishes, and the user
        // wants a direct path to the report page from there.
        const isConversionReady =
          project?.report_type === REPORT_TYPES?.CONVERSION &&
          currentStatusKey === statusKeys.REPORT;
        const isReportActive =
          isReportGenerated ||
          currentStatusKey === statusKeys.APPROVAL ||
          currentStatusKey === statusKeys.SUBMISSION ||
          currentStatusKey === statusKeys.END ||
          period?.transformation_progress_status === 'Completed' ||
          isConversionReady ||
          isNilReturn;
        if (!isReportActive) path = '#';
      }
      if (key === WORKFLOW_STAGE_KEYS.APPROVAL) {
        const approveProgress = (period?.approve_progress_status as string | undefined) ?? '';
        // Once the report file exists the user can already assign approvers,
        // even if comments are still being resolved. The status fn sticks at
        // REPORT in that case (comments check), so we explicitly unlock the
        // Approval path via `isReportGenerated`.
        const isApprovalActive =
          approveProgress === 'Completed' ||
          isReportGenerated ||
          currentStatusKey === statusKeys.APPROVAL ||
          currentStatusKey === statusKeys.SUBMISSION ||
          currentStatusKey === statusKeys.END;
        if (!isApprovalActive) path = '#';
      }
      if (key === WORKFLOW_STAGE_KEYS.SUBMISSION) {
        const submitProgress = (period?.submit_progress_status as string | undefined) ?? '';
        const isSubmissionActive =
          submitProgress === 'Completed' ||
          currentStatusKey === statusKeys.SUBMISSION ||
          currentStatusKey === statusKeys.END;
        if (!isSubmissionActive) path = '#';
      }

      const gate = stageGateFor(key, gatingOptions);
      if (gate) path = '#';

      if (gate) {
        subLabel = undefined;
        tone = 'neutral';
      }

      // Report stage label stays as plain "Report" regardless of generation
      // state; the per-stage status / placeholder already conveys whether
      // the report still needs to be generated.
      const label = stageLabels[key];

      // One-click shortcuts. Only automation / from-template periods can
      // reuse the previous period's setup. Each stage exposes a single
      // shortcut kind; the stepper owns the actual mutation.
      let action: WorkflowStageAction | undefined;
      const isAutomationPeriod = period?.template_type && period.template_type !== 'sratch';
      if (
        key === WORKFLOW_STAGE_KEYS.JURISDICTIONS &&
        isAutomationPeriod &&
        !period?.jurisdiction_count &&
        period?.jurisdiction_validation_progress_status !== 'Completed'
      ) {
        action = {
          kind: 'copy-jurisdictions',
          label: `Copy ${stageLabels[key].toLowerCase()} from previous period`
        };
      } else if (
        key === WORKFLOW_STAGE_KEYS.TRANSFORMATION &&
        isAutomationPeriod &&
        period?.transformation_progress_status === 'Not started'
      ) {
        action = {
          kind: 'automate-transformation',
          label: 'Run automated transformation from previous period'
        };
      }

      return {
        key,
        label,
        path,
        status,
        tone,
        subLabel,
        action,
        isLoading,
        progress,
        locked: !!gate,
        lockReason: gate?.message,
        lockStageKey: gate?.stageKey
      };
    });
  }, [
    periodId,
    workflowId,
    period,
    project,
    jurisdictions,
    isLookupEnabled,
    isSetupEnabled,
    currentUser?.role,
    liveExtractionStats,
    liveTransformationStats,
    mappedPairCount,
    extractionRecordCount,
    periodFileCount,
    transformationCount,
    criticalIssueCount
  ]);

  // Index of the current stage (or last done if everything is finished) — used
  // by the back/next buttons in the WorkflowStepper.
  const currentStageIndex = useMemo(() => {
    if (stages.length === 0) return -1;
    const idx = stages.findIndex(s => s.status === 'current');
    if (idx >= 0) return idx;
    // No 'current' → all done; treat last stage as the cursor.
    return stages.length - 1;
  }, [stages]);

  return {
    stages,
    currentStageIndex,
    isLoading: isLoadingPeriod || isLoadingProject || isLoadingJurisdictions
  };
};
